import triggerTracking from '@blueprint/tracking';
import { FC, MouseEvent, useState } from 'react';
import { Filters } from './Filters';
import { FiltersContainer, StyledFiltersModal } from './Filters.styles';
import { FiltersButton } from './FiltersButton';

const KEYPRESS_ENTER_EVENT = 0;

enum LayoutVersion {
    Mobile = 'mobile',
    Desktop = 'desktop'
}

type TrackingEvents = 'options' | 'filters';
type TrackingEventsClickOption = 'open' | 'close' | 'update';

const triggerGATracking = (
    section: TrackingEvents,
    options: TrackingEventsClickOption
) => {
    const eventCategory =
        section === 'options' ? 'search results' : 'search_filters';
    const eventLabel = `${section}_${options}`;

    return triggerTracking({
        event: 'GAEvent',
        eventAction: 'click',
        eventCategory,
        eventLabel
    });
};

export type FiltersModalProps = {
    version: LayoutVersion;
    testId:
        | 'search-results-header_filters-button'
        | 'mobile-sub-header_filters-button';
};

export const FiltersModal: FC<React.PropsWithChildren<FiltersModalProps>> = ({
    version
}) => {
    const [showModal, setShowModal] = useState(false);

    const closeModal = () => {
        setShowModal(false);
        triggerGATracking('filters' as TrackingEvents, 'close');
        document.body.style.overflow = '';
    };

    const closeModalAfterUpdate = () => {
        setShowModal(false);
        triggerGATracking('filters' as TrackingEvents, 'update');
        document.body.style.overflow = '';
    };

    const openModal = () => {
        setShowModal(true);
        triggerGATracking('filters' as TrackingEvents, 'open');
        document.body.style.overflow = 'hidden';
    };

    const onClick = (evt: MouseEvent<HTMLButtonElement>) => {
        // https://github.com/facebook/react/issues/3907#issuecomment-363948471
        if (evt.detail !== KEYPRESS_ENTER_EVENT) {
            openModal();
        }
    };

    const onKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            e.stopPropagation();
        }
    };

    const onKeyUp = (e: React.KeyboardEvent<HTMLButtonElement>) => {
        if (e.key === 'Enter') {
            openModal();
        }
    };

    return (
        <>
            <FiltersButton
                version={version}
                onClick={onClick}
                onKeyDown={onKeyDown}
                onKeyUp={onKeyUp}
            />
            <StyledFiltersModal isShowing={showModal} close={closeModal}>
                <FiltersContainer>
                    <Filters
                        onClose={closeModal}
                        onUpdate={closeModalAfterUpdate}
                    />
                </FiltersContainer>
            </StyledFiltersModal>
        </>
    );
};
